import React, { Component } from 'react';
import { Button, ButtonGroup, Card, Container, Row, Col } from 'react-bootstrap';
import { GoPlus, GoSidebarExpand } from 'react-icons/go';
import {
  listMissions,
  getMission,
  listCells,
  listDependsOn,
  getCell,
  upsertCell,
  removeCell,
  addDependsOnEdge,
  removeDependsOnEdge,
} from '../../domain/model';
import MissionGraph, { edgeRfId, newCellPosition } from './missionGraph';
import MissionBoard from './missionBoard';
import MissionsRail from './missionsRail';
import PaneResizer from './paneResizer';
import {
  RAIL_WIDTH_KEY, PANEL_WIDTH_KEY, MIN_PANE, MIN_BOARD, BOARD_VIEWS, readPaneWidth, writePaneWidth, readBoardView, writeBoardView,
} from './paneWidths';
import DeleteCellDialog from './deleteCellDialog';
import { UNTITLED } from '../../domain/schema';
import { classifyCellObjects, describeCellObjects } from '../../domain/cellObjects';
import CellPanel from './cellPanel';
import CellDetailSurface from '../cell';
import MissionPanel from './missionPanel';
import DependencyPanel from './dependencyPanel';
import WidgetStatus from '../../../common/widgetStatus';
import Tooltip from '../../../../../controls/common/tooltip';
import { loadingText } from './boardWaits';
import './cells.css';

/**
 * CellsSurface — the core surface of Mission Control.
 *
 * Layout:
 *   [ Card: missions rail ][ Card: MissionGraph (flex 1) ][ Card: detail panel (right dock) ]
 *
 * The detail panel is ALWAYS rendered while a mission is active and shows one of three
 * views depending on the selection: a selected cell → CellPanel, a selected dependency
 * edge → DependencyPanel, nothing selected → MissionPanel (mission summary). Deselecting
 * therefore never hides the panel — it falls back to the Mission view.
 *
 * Both creation controls are driven by a door the node hands over (onCreateCell / onCreateMission):
 * New Cell sits at the board's bottom centre beside the Graph/Board toggle, New Mission in the rail's
 * own Actions section. This surface renders the missions rail, the way back to it while it is away,
 * the board's bottom-centre group, the board itself (graph or board) and the detail dock, with a drag
 * handle on the rail and on the dock.
 *
 * Selection state flows through the shell's selection/onSelect props so that
 * surface-switching preserves selection. The selection shape is:
 *   { missionId: string, cellId: string|null, edge: object|null }
 * Only one of cellId / edge is ever set.
 *
 * Every write here is INCREMENTAL: it calls the client, then applies a pure
 * store reducer through onApplyLocal — never onReload. The mission graph updates
 * in place (no re-fetch, no re-layout, no loading spinner). The editing handlers
 * are stable instance methods that read the current mission id from props at call
 * time, so the MissionGraph prop chain stays referentially stable across renders.
 *
 * Props:
 *   store        — model store (from MissionsNode state)
 *   client       — MissionsClient instance
 *   selection    — { missionId?, cellId?, edge? } | null
 *   onSelect     — (item) => void
 *   onApplyLocal — (updater: store => store) => void  — incremental local store update
 *   loading, error  — the board's own status; drawn in the board column so the rail stays up
 *   loadingWhat  — which wait `loading` is (boardWaits.js's tokens), so the status box names it
 *   onCreateMission — () => void — the rail's + New Mission
 *   creatingMission — a creation is in flight; the rail disables its control
 *   onCreateCell — () => void — the board's + New Cell, ABSENT when there is nothing to add a cell to.
 *                  Its presence IS the gate: the node owns it (missions/index.js), because the handler
 *                  it hands over resolves the mission from the same selection the gate reads, and a
 *                  second rule here could offer a button whose handler then refuses.
 *   creatingCell — a creation is in flight; the control is disabled rather than absent, because this
 *                  one is blocked by something that is about to finish
 *   scope        — { orgId, blobId } | undefined — passed through to CellDetailSurface for
 *                  blob-scoped preferences (e.g. the artifact zoom level)
 */
class CellsSurface extends Component {
  constructor(props) {
    super(props);
    this._mounted = false;
    // Local UI state: the Mission panel's × collapses the whole detail dock for more graph room.
    // Selecting a cell/edge re-opens it; a plain deselect (pane click) does NOT.
    // `deleting` is the whole of the delete dialog's state: which cell, what goes with it, how far
    // along, and what went wrong. Null when no delete is being confirmed or run.
    this.state = {
      panelHidden: false,
      openCellId: null,
      deleting: null,
      // The rail's own two: whether it is up, and how wide. Hidden is local UI state like
      // panelHidden beside it; the width is remembered per blob (paneWidths.js). Both the rail's
      // header × and the board's way back write it — one fact, two places to reach it from.
      railHidden: false,
      railWidth: readPaneWidth(props.scope, RAIL_WIDTH_KEY),
      panelWidth: readPaneWidth(props.scope, PANEL_WIDTH_KEY),
      boardView: readBoardView(props.scope),
    };
    this._rowRef = React.createRef();
    this._railRef = React.createRef();
    this._dockRef = React.createRef();
    this._railBounds = () => this._boundsExcluding(this._dockRef);
    this._railMeasure = () => this._measure(this._railRef);
    this._onRailResize = (railWidth) => this.setState({ railWidth });
    // The width is already on screen: onResize set it on every frame, and the commit carries the last one.
    this._onRailCommit = (width) => writePaneWidth(this.props.scope, RAIL_WIDTH_KEY, width);
    this._dockBounds = () => this._boundsExcluding(this._railRef);
    this._dockMeasure = () => this._measure(this._dockRef);
    this._onDockResize = (panelWidth) => this.setState({ panelWidth });
    // The setState is redundant only while onResize sets the same value; if that stops, this is the only setter.
    this._onDockCommit = (width) => {
      this.setState({ panelWidth: width });
      writePaneWidth(this.props.scope, PANEL_WIDTH_KEY, width);
    };
  }

  // The pane's rendered width, for a gesture that starts from the stylesheet's value rather than a
  // stored one.
  _measure(ref) {
    return ref.current ? Math.round(ref.current.getBoundingClientRect().width) : MIN_PANE;
  }

  // How wide this pane may go: the row, less the other pane as it is now, less the board's floor.
  // Measured when the gesture starts, because the row changes size without this component rendering.
  _boundsExcluding(otherRef) {
    const total = this._rowRef.current ? this._rowRef.current.getBoundingClientRect().width : 0;
    const other = otherRef.current ? otherRef.current.getBoundingClientRect().width : 0;
    return { min: MIN_PANE, max: Math.max(MIN_PANE, total - other - MIN_BOARD) };
  }

  // The dock's inline width, or nothing. `.detail-panel-col`'s 400px cap is global to the app (src/index.css),
  // so the width and the release of the cap are one expression; shrink stays 1 so the dock is what yields.
  _dockStyle() {
    const { panelWidth } = this.state;
    return panelWidth ? { flex: `0 1 ${panelWidth}px`, maxWidth: 'none' } : undefined;
  }

  // Drawn only when there is something to switch. It used to have an invisible twin holding its place
  // while the board was loading, failed or empty; the controls float over the board now, so there is no
  // height to hold and nothing below them to keep still.
  _renderViewToggle() {
    return (
      <ButtonGroup size="sm" className="mc-board-views">
        {BOARD_VIEWS.map(view => (
          <Button key={view} variant={view === this.state.boardView ? 'secondary' : 'light'}
            active={view === this.state.boardView}
            onClick={() => this.handleBoardView(view)}>
            {view.charAt(0).toUpperCase() + view.slice(1)}
          </Button>
        ))}
      </ButtonGroup>
    );
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  // The surface stays mounted across a session change, so an open cell's id outlives the mission it belonged to
  // and would re-open unasked when that mission returns.
  componentDidUpdate(prevProps) {
    const was = prevProps.selection && prevProps.selection.missionId;
    const now = this.props.selection && this.props.selection.missionId;
    if (was !== now && this.state.openCellId) this.setState({ openCellId: null });
  }

  // ---------------------------------------------------------------------------
  // Mission resolution — prefer the selected mission, fall back to the first one
  // ---------------------------------------------------------------------------

  _missionId() {
    const { store, selection } = this.props;
    const missions = store ? listMissions(store) : [];
    return (selection && selection.missionId) || (missions.length > 0 ? missions[0].id : null);
  }

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------

  // Select a cell (clearing any edge selection). A null cellId — e.g. a pane click — fully deselects,
  // which falls back to the Mission panel.
  handleSelectCell = (cellId) => {
    const { onSelect } = this.props;
    // A real selection re-opens a collapsed dock; a pane-click deselect (cellId null) leaves it as-is.
    if (cellId && this.state.panelHidden) this.setState({ panelHidden: false });
    if (onSelect) onSelect({ missionId: this._missionId(), cellId: cellId || null, edge: null });
  };

  // Select a dependency edge (clearing any cell selection).
  handleSelectEdge = (edge) => {
    const { onSelect } = this.props;
    if (edge && this.state.panelHidden) this.setState({ panelHidden: false });
    if (onSelect) onSelect({ missionId: this._missionId(), cellId: null, edge: edge || null });
  };

  // Putting the rail away and bringing it back: the rail's header × and the board's way back are the
  // two ends of this one handler.
  handleToggleRail = () => {
    this.setState(state => ({ railHidden: !state.railHidden }));
  };

  // Picking a mission in the rail. It clears any cell/edge selection, because neither belongs to the
  // mission being moved to, and goes through the surface's one `onSelect` so the node's own door
  // (handleSelect) does the watching, remembering and sweeping.
  handleSelectMission = (missionId) => {
    const { onSelect } = this.props;
    if (onSelect) onSelect({ missionId, cellId: null, edge: null });
  };

  // Close the cell / dependency panel — deselect everything, which falls back to the Mission panel.
  handleClosePanel = () => {
    const { onSelect } = this.props;
    if (onSelect) onSelect({ missionId: this._missionId(), cellId: null, edge: null });
  };

  // ---------------------------------------------------------------------------
  // Incremental edits — client write, then onApplyLocal(pure reducer)
  // ---------------------------------------------------------------------------

  // Wire a dependency: dependent depends on prerequisite (the arrow is drawn dependent → prerequisite,
  // arrowhead on the prerequisite).
  handleAddDependency = async (dependentId, prerequisiteId) => {
    const { client, onApplyLocal, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    // Never create a DUPLICATE dependency. The local reducer dedups by (from,to), but the server write
    // does not — so re-adding an existing pair would commit a second edge that surfaces on the next
    // reload. Bail out entirely (no server write) when the edge already exists.
    const existing = store ? listDependsOn(store, missionId) : [];
    if (existing.some(e => e.from_id === dependentId && e.to_id === prerequisiteId)) return;
    try {
      const edge = await client.addDependsOn(missionId, dependentId, prerequisiteId);
      if (onApplyLocal) onApplyLocal(s => addDependsOnEdge(s, missionId, edge));
    } catch (_e) {
      // Ignore; the graph reflects server state on the next genuine reload.
    }
  };

  // Add a DEPENDENT cell — a new cell that depends on this one (this cell is the prerequisite) — and
  // wire "new depends on this" in one gesture. Both client calls are awaited first, then the new cell
  // AND its depends_on edge are applied to the store in a SINGLE local update so the node and its edge
  // appear together in one refresh — not as two separate pops. The new cell is placed to the RIGHT of
  // its parent (and that x/y persisted) so it lands beside it rather than at the dagre origin; parentPos
  // is the parent's current rendered position, threaded from CellNode.
  handleAddDependent = async (cellId, parentPos) => {
    const { client, onApplyLocal, onSelect } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    try {
      // The parent keeps its place: see pinCell.
      await this.pinCell(cellId, parentPos);
      const pos = newCellPosition(parentPos, 'dependent');
      const dependent = await client.addCell(missionId, {
        title: UNTITLED,
        // New cells default both structural choices to inherit-from-mission.
        workspace_mode: 'inherit_from_mission',
        cell_workflow_mode: 'inherit_from_mission',
        ...(pos || {}),
      });
      // new cell (dependent) depends on cellId (prerequisite): from_id = new, to_id = this.
      const edge = await client.addDependsOn(missionId, dependent.element_id, cellId);
      if (onApplyLocal) {
        onApplyLocal(s => addDependsOnEdge(upsertCell(s, missionId, dependent), missionId, edge));
      }
      if (onSelect) onSelect({ missionId, cellId: dependent.element_id, edge: null });
    } catch (_e) {
      // Ignore; reflected on next reload.
    }
  };

  // Add a PREREQUISITE cell — a new cell that this one depends on (the new cell is the prerequisite) —
  // and wire "this depends on new" in one gesture. Mirrors handleAddDependent but with the dependency
  // pointing the other way and the new cell placed to the LEFT of its parent; the new cell + its edge
  // are applied in a SINGLE local update.
  handleAddPrerequisite = async (cellId, parentPos) => {
    const { client, onApplyLocal, onSelect } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    try {
      await this.pinCell(cellId, parentPos);
      const pos = newCellPosition(parentPos, 'prerequisite');
      const prerequisite = await client.addCell(missionId, {
        title: UNTITLED,
        // New cells default both structural choices to inherit-from-mission.
        workspace_mode: 'inherit_from_mission',
        cell_workflow_mode: 'inherit_from_mission',
        ...(pos || {}),
      });
      // cellId (dependent) depends on the new cell (prerequisite): from_id = this, to_id = new.
      const edge = await client.addDependsOn(missionId, cellId, prerequisite.element_id);
      if (onApplyLocal) {
        onApplyLocal(s => addDependsOnEdge(upsertCell(s, missionId, prerequisite), missionId, edge));
      }
      if (onSelect) onSelect({ missionId, cellId: prerequisite.element_id, edge: null });
    } catch (_e) {
      // Ignore; reflected on next reload.
    }
  };

  /**
   * Pin a cell where it is currently drawn, if nothing has pinned it yet.
   *
   * A cell with no persisted x/y gets a DAGRE slot, and dagre re-runs whenever any cell in the graph
   * lacks coordinates — so an unpinned cell is re-slotted every time the graph changes shape. Adding
   * a neighbour changes the shape, which is why adding one to the root appeared to MOVE the root: it
   * had never been dragged, so it had no coordinates of its own to keep.
   *
   * Writing the position it is already drawn at is visually a no-op and makes it stay there. Called
   * with the parent's live rendered position, which the node hands to the add gesture anyway.
   */
  pinCell = async (cellId, position) => {
    const { client, onApplyLocal, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId || !position || position.x == null || position.y == null) return;
    const cell = store ? getCell(store, missionId, cellId) : null;
    if (!cell) return;
    const props = cell.props || {};
    if (props.x != null && props.y != null) return;   // already pinned — leave it exactly as it is
    const merged = { ...cell, props: { ...props, x: position.x, y: position.y } };
    if (onApplyLocal) onApplyLocal(s => upsertCell(s, missionId, merged));
    try {
      await client.setCellProps(missionId, cellId, { x: position.x, y: position.y });
    } catch (_e) {
      // Ignore; the optimistic local position stands until the next reload.
    }
  };

  // Persist a dragged cell's new position (optimistic local upsert + server write) so the graph never
  // re-layouts that cell on later edits.
  handleMoveCell = async (cellId, position) => {
    const { client, onApplyLocal, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId || !position) return;
    const cell = store ? getCell(store, missionId, cellId) : null;
    if (!cell) return;
    const merged = { ...cell, props: { ...(cell.props || {}), x: position.x, y: position.y } };
    if (onApplyLocal) onApplyLocal(s => upsertCell(s, missionId, merged));
    try {
      await client.setCellProps(missionId, cellId, { x: position.x, y: position.y });
    } catch (_e) {
      // Ignore; the optimistic local position stands until the next reload.
    }
  };

  /**
   * Remove a cell — the vertex, and everything the cell owns in the session namespace.
   *
   * A cell that owns nothing goes straight away: the only thing lost is an empty cell, and a dialog
   * to say so is furniture. A cell that owns a thread or an artifact is confirmed first, because
   * what goes with it is work an agent did and the record of why, and none of it comes back.
   */
  handleRemoveCell = async (cellId) => {
    const { client, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    const groups = classifyCellObjects(client.listCellObjectAliases(missionId, cellId));
    if (groups.total === 0) return this._deleteCell(cellId);
    const cell = store ? getCell(store, missionId, cellId) : null;
    this.setState({
      deleting: {
        cellId,
        missionId,
        title: (cell && cell.props && cell.props.title) || cellId,
        summary: describeCellObjects(groups),
        total: groups.total,
        progress: null,
        error: null,
      }
    });
  };

  // The confirmed path: the cell's objects first, then the vertex.
  //
  // That order is the recoverable one. Delete the vertex first and a failure halfway through the
  // objects leaves them with nothing pointing at them — invisible, and findable only by knowing a
  // dead cell's id. This way a failure leaves the CELL, so the graph still shows what is left and
  // pressing delete again resumes; which is also why a partial failure stops rather than removing
  // the vertex anyway.
  _deleteCellWithObjects = async () => {
    const { client } = this.props;
    // The mission the question was asked about, not the one on screen now: the dialog outlives a mission change.
    const { cellId, missionId } = this.state.deleting;
    // Seeded with the count already known, so the alert is never a frame of "0/0". The first real
    // event lands in the same tick and adds the alias.
    this.setState(state => ({
      deleting: { ...state.deleting, progress: { done: 1, total: state.deleting.total } }
    }));
    let result;
    try {
      result = await client.deleteCellObjects(missionId, cellId, this._recordDeleteProgress);
    } catch (e) {
      return this._deleteFailed(e && e.message ? e.message : 'Could not delete this cell.');
    }
    if (result.failed.length > 0) {
      return this._deleteFailed(
        `Kept the cell: ${result.failed.length} of ${result.failed.length + result.deleted.length} ` +
        'objects could not be deleted. Try again.'
      );
    }
    await this._deleteCell(cellId, missionId);
    if (this._mounted) this.setState({ deleting: null });
  };

  // One event per object, as it starts — which is the tense the alert is written in.
  _recordDeleteProgress = (progress) => {
    if (!this._mounted) return;
    this.setState(state => (state.deleting ? { deleting: { ...state.deleting, progress } } : null));
  };

  _deleteFailed = (message) => {
    if (!this._mounted) return;
    this.setState(state => (state.deleting ? { deleting: { ...state.deleting, error: message } } : null));
  };

  // The vertex itself, and the local reflections of its removal.
  _deleteCell = async (cellId, missionId = this._missionId()) => {
    const { client, onApplyLocal, onSelect, selection } = this.props;
    // Delete UNCONDITIONALLY — no if_rev guard. The store's cell.rev goes stale the moment the cell is
    // edited (set_vertex_props is itself unconditional and _applyLocalProps keeps the old rev) or the
    // heartbeat updates the cell server-side; a stale if_rev would make the server reject the delete
    // while the optimistic local removal still runs, so the cell "comes back" on the next reload.
    try {
      await client.deleteCell(missionId, cellId);
    } catch (_e) {
      // Ignore; reflected on next reload.
    }
    if (onApplyLocal) onApplyLocal(s => removeCell(s, missionId, cellId));
    if (selection && selection.cellId === cellId && onSelect) {
      onSelect({ missionId, cellId: null, edge: null });
    }
  };

  // Remove a dependency edge (identified by element_id; located in the store by from/to). When the
  // removed edge is the selected one, deselect so the panel falls back to the Mission view.
  handleRemoveEdge = async (edge) => {
    const { client, onApplyLocal, onSelect, selection, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId || !edge) return;
    // Delete EVERY server edge for this (from,to) pair — not just the one passed — so any pre-existing
    // duplicates are cleaned up in a single remove and don't resurface on the next reload.
    const all = store
      ? listDependsOn(store, missionId).filter(e => e.from_id === edge.from_id && e.to_id === edge.to_id)
      : [];
    const targets = all.length ? all : [edge];
    for (const e of targets) {
      try {
        // Unconditional delete (no if_rev) — same stale-rev reason as handleRemoveCell.
        if (e.element_id) await client.deleteEdge(missionId, e.element_id);
      } catch (_e) {
        // Ignore; reflected on next reload.
      }
    }
    if (onApplyLocal) onApplyLocal(s => removeDependsOnEdge(s, missionId, edge.from_id, edge.to_id));
    const sel = selection && selection.edge;
    if (sel && sel.from_id === edge.from_id && sel.to_id === edge.to_id && onSelect) {
      onSelect({ missionId, cellId: null, edge: null });
    }
  };

  // Which board is up. Remembered per blob: a view toggle that forgets which view you work in is a
  // control people press twice on every visit.
  handleBoardView = (boardView) => {
    this.setState({ boardView });
    writeBoardView(this.props.scope, boardView);
  };

  handleOpenCell = (cellId) => this.setState({ openCellId: cellId });

  handleBackToMission = () => this.setState({ openCellId: null });

  render() {
    const { store, client, selection, onApplyLocal, canEdit, scope } = this.props;
    const { deleting } = this.state;

    // Resolve mission id: prefer the one in selection, fall back to first available
    const missions = store ? listMissions(store) : [];
    const selMissionId = this._missionId();
    // Every graph gesture that writes is passed as a handler, and MissionGraph draws an affordance
    // only for a handler it was given (see CellNode's toolbar, and GraphCanvas's "absent props keep
    // the canvas static"). So withholding them here is the whole of read-only on the canvas: no
    // toolbar buttons, no connectable handles, no draggable nodes. `canEdit` defaults to true so an
    // embedding that does not express the seam behaves as it did.
    const editable = !!selMissionId && canEdit !== false;
    const selCellId = (selection && selection.cellId) || null;
    const selEdge = (selection && selection.edge) || null;

    // Cells and dependency edges for the active mission
    const cells = (store && selMissionId) ? listCells(store, selMissionId) : [];
    const dependsOnEdges = (store && selMissionId) ? listDependsOn(store, selMissionId) : [];

    // Selected cell vertex / active mission vertex / selected edge id
    const selectedCell = (store && selMissionId && selCellId)
      ? getCell(store, selMissionId, selCellId)
      : null;
    const mission = (store && selMissionId) ? getMission(store, selMissionId) : null;
    const selectedEdgeId = selEdge ? edgeRfId(selEdge) : null;

    // The detail dock shows unless the user collapsed it (Mission panel ×) AND nothing is selected.
    // A live cell/edge selection always shows its panel.
    const showPanel = !!(selectedCell || selEdge || !this.state.panelHidden);

    // Drilling into a cell takes the whole surface. The mission map answers "what is the shape of
    // this work"; a cell answers "what is happening inside this piece of it", and that needs a tree
    // and a transcript rather than the dock beside the graph.
    const openCell = (store && selMissionId && this.state.openCellId)
      ? getCell(store, selMissionId, this.state.openCellId)
      : null;
    if (openCell) {
      return (
        <CellDetailSurface
          key={openCell.element_id}
          cell={openCell}
          missionId={selMissionId}
          client={client}
          templates={store ? store.templates : {}}
          onBack={this.handleBackToMission}
          // The cell screen edits a draft's details and workflow now, so it needs the same local
          // reflection the dock has: a write shows up without waiting for a reload, and the graph
          // behind it agrees with the panel in front of it.
          onApplyLocal={onApplyLocal}
          canEdit={editable}
          scope={scope}
        />
      );
    }

    // No missions is not a reason to withhold the rail: + New Mission lives there, and it is the only
    // door to a first mission — an early return here used to be a screen with no way off it.
    const noMissions = 0 === missions.length;
    // With no session there is nothing to have missions IN, so "No missions yet." is an answer to a
    // question nobody asked and + New Mission is a door that cannot open (canMutateSession is false
    // without an open session). The sentence is the app's own for this state — the thread and graph
    // widgets say exactly this — so a reader meets one wording across the playground.
    const noSession = false === this.props.hasSession;
    // Loading or failed: the board column says so, and the rail beside it stays. The dock describes a
    // mission that is not on screen yet, so it waits too.
    const { loading, error } = this.props;
    const boardStatus = !!(loading || error);
    // A board is up when it is drawing a mission rather than a sentence about one. Both of the trailing
    // controls answer to that one fact: there is nothing to switch, and nothing to add a cell to, while
    // what fills the pane is a status box — and a control floating over the first words of one is the
    // defect the status box's own clearance cannot fix, because it clears the strip's HEIGHT, not its
    // width. The door to a new cell additionally needs the node to have handed one over (the props note
    // above).
    const boardIsUp = !boardStatus && !noMissions && !noSession;
    // The rail and its way back are two states of one thing: exactly one of them is on screen — and
    // with no session, NEITHER is. A rail with no session is a frame around a list that cannot have
    // rows and a door (+ New Mission) that cannot open, and the way back leads to it; the board's own
    // sentence is the whole of what this state has to say (item 24).
    const railUp = !this.state.railHidden && !noSession;
    const showRail = this.state.railHidden && !noSession;
    const railLabel = 'Show the missions';
    const canSwitchView = boardIsUp;
    const canCreateCell = boardIsUp && !!this.props.onCreateCell;
    const hasBoardControls = canCreateCell || canSwitchView;

    return (
      <Container fluid className="mc-cells-container">
        <Row className="mc-grid-row" ref={this._rowRef}>
          {railUp && (
          <Col className="d-flex flex-column mc-missions-rail-col" ref={this._railRef}
            style={this.state.railWidth ? { flex: `0 1 ${this.state.railWidth}px` } : undefined}>
            <Card className="mc-panel-card">
              {/* .mc-rail is missionControl.css's own padded scroll region. It and the global
                  .card-body rule are both (0,1,0) and both state var(--bh-inset), so the tie
                  src/index.css wins resolves to the same value either way. */}
              <Card.Body className="mc-rail">
                <MissionsRail
                  store={store}
                  selectedId={selMissionId}
                  canEdit={canEdit}
                  creating={this.props.creatingMission}
                  onSelect={this.handleSelectMission}
                  onCreate={this.props.onCreateMission}
                  onHide={this.handleToggleRail}
                />
              </Card.Body>
            </Card>
            <PaneResizer side="right" label="Resize the missions rail"
              width={this.state.railWidth} measure={this._railMeasure} bounds={this._railBounds}
              onResize={this._onRailResize} onCommit={this._onRailCommit} />
          </Col>
          )}

          {/* Mission graph — full-bleed canvas in a flat card */}
          <Col className="d-flex flex-column mc-board-col">
            <Card className="mc-graph-card">
              <Card.Body className="mc-card-flush">
                {/* THE STRIP IS THE WAY BACK, and nothing else. Hiding the rail is the rail's own
                    header × (missionsRail.js) — the control every other panel here puts away with —
                    so the strip is drawn exactly while the rail is away, over the left edge it
                    reappears at. It is NOT gated on the board being up: it is the only door back,
                    and a failed board is terminal. While the rail is up the pane's first child is
                    whatever the board draws, which is what the
                    `.mc-board-head + .playground-widget-status` clearance reads — adjacency is the
                    whole of that rule, so a sentence takes the clearance exactly when a strip is
                    there to clear. */}
                {showRail && (
                <div className="mc-board-head">
                  <div className="mc-board-head-lead">
                    <ButtonGroup size="sm">
                      <Tooltip text={railLabel}>
                        <Button variant="light" className="mc-rail-toggle" aria-label={railLabel}
                          onClick={this.handleToggleRail}>
                          <GoSidebarExpand />
                        </Button>
                      </Tooltip>
                    </ButtonGroup>
                  </div>
                </div>
                )}
                {/* THE TWO ACTS SIT AT THE BOTTOM CENTRE, ALWAYS EXPANDED. They used to share the top
                    strip, where they covered a lane heading at every width; collapsing them behind a
                    `···` cleared the heading by hiding the actions, which is what the owner rejected
                    — "does not solve its issues and hides actions".
                    ANCHORED TO THE PANE, not to the content: absolutely positioned against
                    `.mc-card-flush`, so neither the lanes' horizontal scroll nor a lane body's
                    vertical scroll can carry it off. That is the property being bought — the place is
                    always available. Drawn only where there is a board to act on, the same rule the
                    trailing controls always had, so it is never over a status box. */}
                {hasBoardControls && (
                  <div className="mc-board-foot">
                    {canCreateCell && (
                      <ButtonGroup size="sm">
                        <Button variant="light" aria-label="New Cell"
                          disabled={!!this.props.creatingCell} onClick={this.props.onCreateCell}>
                          <GoPlus /> New Cell
                        </Button>
                      </ButtonGroup>
                    )}
                    {canSwitchView && this._renderViewToggle()}
                  </div>
                )}
                {loading ? (
                  <WidgetStatus variant="primary" loading>
                    {loadingText(this.props.loadingWhat)}
                  </WidgetStatus>
                ) : error ? (
                  <WidgetStatus variant="warning">{error}</WidgetStatus>
                ) : noSession ? (
                  <WidgetStatus variant="info">Select a session to view this widget.</WidgetStatus>
                ) : noMissions ? (
                  <WidgetStatus variant="info">
                    {false === canEdit
                      ? 'No missions yet.'
                      : 'No missions yet. Use + New Mission in the missions rail.'}
                  </WidgetStatus>
                ) : 'board' === this.state.boardView ? (
                  <MissionBoard
                    cells={cells}
                    dependsOnEdges={dependsOnEdges}
                    selectedId={selCellId}
                    onSelect={this.handleSelectCell}
                    onOpen={this.handleOpenCell}
                  />
                ) : (
                <MissionGraph
                  key={selMissionId}
                  cells={cells}
                  dependsOnEdges={dependsOnEdges}
                  selectedId={selCellId}
                  selectedEdgeId={selectedEdgeId}
                  workspaces={store && store.templates ? store.templates.workspaces : []}
                  onSelect={this.handleSelectCell}
                  onOpen={this.handleOpenCell}
                  onSelectEdge={this.handleSelectEdge}
                  onAddDependency={editable ? this.handleAddDependency : undefined}
                  onAddPrerequisite={editable ? this.handleAddPrerequisite : undefined}
                  onAddDependent={editable ? this.handleAddDependent : undefined}
                  onRemoveCell={editable ? this.handleRemoveCell : undefined}
                  onRemoveEdge={editable ? this.handleRemoveEdge : undefined}
                  onMoveCell={editable ? this.handleMoveCell : undefined}
                />
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Detail dock — shown unless collapsed via the Mission panel's ×. One of: the selected
              cell (CellPanel), the selected dependency edge (DependencyPanel), or the mission summary
              (MissionPanel) when nothing is selected. */}
          {showPanel && !noMissions && !noSession && !boardStatus && (
          <Col className="d-flex flex-column detail-panel-col mc-dock-col" ref={this._dockRef}
            style={this._dockStyle()}>
            <Card className="mc-panel-card">
              <Card.Body>
                {selectedCell ? (
                  <CellPanel
                    key={selectedCell.element_id}
                    cell={selectedCell}
                    missionId={selMissionId}
                    client={client}
                    onClose={this.handleClosePanel}
                    onOpen={() => this.handleOpenCell(selectedCell.element_id)}
                    onApplyLocal={onApplyLocal}
                    templates={store ? store.templates : {}}
                    canEdit={editable}
                  />
                ) : selEdge ? (
                  <DependencyPanel
                    key={selectedEdgeId}
                    edge={selEdge}
                    cells={cells}
                    onClose={this.handleClosePanel}
                  />
                ) : (
                  <MissionPanel
                    key={selMissionId}
                    mission={mission}
                    missionId={selMissionId}
                    cells={cells}
                    client={client}
                    onApplyLocal={onApplyLocal}
                    onClose={() => this.setState({ panelHidden: true })}
                    templates={store ? store.templates : {}}
                    canEdit={editable}
                  />
                )}
              </Card.Body>
            </Card>
            <PaneResizer side="left" label="Resize the cell panel"
              width={this.state.panelWidth} measure={this._dockMeasure} bounds={this._dockBounds}
              onResize={this._onDockResize} onCommit={this._onDockCommit} />
          </Col>
          )}
        </Row>

        {/* Confirming (and then watching) a cell delete that takes threads and artifacts with it.
            Mounted on the surface rather than on the node that starts it, because the node is
            removed by the very action the dialog is reporting on. */}
        {deleting && (
          <DeleteCellDialog
            show
            title={deleting.title}
            summary={deleting.summary}
            progress={deleting.progress}
            error={deleting.error}
            onConfirm={this._deleteCellWithObjects}
            onHide={() => this.setState({ deleting: null })}
          />
        )}
      </Container>
    );
  }
}

export default CellsSurface;
